import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/Printing/printingSlice";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import {
  PrintCertificate,
  PreviewCertificate,
  uploadKalyanCertificate,
} from "./api";
import {
  useGetCertificateListsQuery,
  useGetRecheckCertificateListsQuery,
} from "../../../../store/queries/Printing";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import moment from "moment/moment";
import { useRef, useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import domtoimage from "dom-to-image-more";
import {
  DEFAULT_PAPER_SIZE,
  DEFAULT_PAPER_SIZE_ID,
  getPaperConfig,
  getPrintItemLimit,
} from "../CertificateTypes/VisitingCardOne/paperSize";
import jsPDF from "jspdf";
import Assets from "../../../../assets/Assets";
const usePrintType = ({
  visitingOneFrontPrintRef,
  visitingOneBackPrintRef,
  papperFrontPrintRef,
  papperBackPrintRef,
  bigFrontPrintRef,
  foldableCardRef,
  vistingDuplexRef,
  papperDuplexRef,
}) => {
  const {
    selectedItems,
    selectedId,
    isMultiPrint,
    printingData,
    cardReissueData,
  } = useSelector((state) => state.printing);
  const selectedCardData = cardReissueData?.Pending?.find(
    (item) => item._id === selectedId?.[0],
  );

  const { data: basicData } = useGetRecheckCertificateListsQuery({
    id: selectedId,
    is_bulk: isMultiPrint,
  });

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const pdfObjRef = useRef(null);
  const filenameRef = useRef("");
  const submittedDataRef = useRef(null);
  const [previewState, setPreviewState] = useState({
    show: false,
    blobUrl: "",
    title: "",
  });

  const showPdfPreview = (pdfObj, title, filename) => {
    pdfObjRef.current = pdfObj;
    filenameRef.current = filename || "";
    const blobUrl = pdfObj.output("bloburl");
    setPreviewState({ show: true, blobUrl, title });
    setIsLoading(false);
  };

  const handleConfirmPrint = () => {
    // Trigger the actual certificate creation/download on the server only
    // once the user confirms via "Print & Upload".
    if (submittedDataRef.current) {
      PrintCertificate(submittedDataRef.current);
    }
    const pdfObj = pdfObjRef.current;
    if (pdfObj) {
      pdfObj.autoPrint();
      window.open(pdfObj.output("bloburl"), "F");
      if (filenameRef.current) {
        pdfObj.save(filenameRef.current);
      }
    }
    if (previewState.blobUrl) {
      try {
        URL.revokeObjectURL(previewState.blobUrl);
      } catch (e) {}
    }
    setPreviewState({ show: false, blobUrl: "", title: "" });
    pdfObjRef.current = null;
    filenameRef.current = "";
    submittedDataRef.current = null;
    handleCloseModal();
  };

  const handleCancelPreview = () => {
    if (previewState.blobUrl) {
      try {
        URL.revokeObjectURL(previewState.blobUrl);
      } catch (e) {}
    }
    setPreviewState({ show: false, blobUrl: "", title: "" });
    pdfObjRef.current = null;
    filenameRef.current = "";
    setIsLoading(false);
  };

  const validationSchema = Yup.object({
    certificate_type: Yup.object().required("Select Certificate Type"),
    report_id: Yup.object().required("Select report Type"),
    side: Yup.object().test(
      "conditional-validation",
      "Select Side of Certificate",
      function (value) {
        const certificateTypeId = this.parent.certificate_type?._id;
        if (certificateTypeId === 1 || certificateTypeId === 3) {
          return value !== undefined && value !== null;
        }
        return true; // No validation if certificate_type is not 1 or 3
      },
    ),
    card_size: Yup.object().test(
      "conditional-validation",
      "Select Card Size of Certificate",
      function (value) {
        const certificateTypeId = this.parent.certificate_type?._id;
        if (certificateTypeId === 1) {
          return value !== undefined && value !== null;
        }
        return true; // No validation if certificate_type is not 1
      },
    ),
    paper_size: Yup.object().test(
      "conditional-validation",
      "Select Paper Size of Certificate",
      function (value) {
        const certificateTypeId = this.parent.certificate_type?._id;
        if (certificateTypeId === 1) {
          return value !== undefined && value !== null;
        }
        return true; // No validation if certificate_type is not 1
      },
    ),
  });
  const formik = useFormik({
    initialValues: {
      certificate_type: "",
      report_id: "",
      side: "",
      remarks: "",
      card_size: {
        name: "Normal (7.8)",
        id: 1,
      },
      paper_size: DEFAULT_PAPER_SIZE,
      certificate_format: "",
    },
    validationSchema: validationSchema,
    validate: (values) => {
      let errors = {};
      // Certificate format is now optional - system generates both PDF and ZIP
      // automatically for card certificates (type 1, side 1) and big cards (type 2)
      return errors;
    },
    onSubmit: (values) => {
      // Card-Certificate item-count guard (see PRINT_ITEM_LIMITS). Safety net in case the
      // disabled Print button is bypassed; the inline error is shown in the modal.
      const limit =
        values?.certificate_type?._id === 1 && values?.side?.id
          ? getPrintItemLimit(values?.paper_size?.id, values?.side?.id)
          : null;
      if (limit != null && (selectedItems?.length ?? 0) > limit) {
        toast.error(
          `Maximum ${limit} items allowed for ${values?.paper_size?.name ?? ""} ${
            values?.side?.id === 3 ? "duplex" : "front/back"
          }.`,
        );
        return;
      }
      setIsLoading(true);
      let data = {
        item_details_id: selectedItems,
        report_id: values?.report_id?._id ?? "",
        certificate_type: values?.certificate_type?._id,
        certificate_format: values?.certificate_format?.id || 1,
        remarks: values?.remarks,
        card_size: values?.card_size?.id ?? 1,
        side: values?.side?.id ?? "",
      };
      // Keep the payload so "Print & Upload" can trigger the create/download API.
      submittedDataRef.current = data;
      PreviewCertificate(data).then((response) => {
        if (response?.data?.status_code === 200) {
          dispatch(
            updateConfig((state) => {
              state.printingData = response?.data?.data;
              state.printUI = true;
              state.clear = true;
            }),
          );

          // Generate both PDF and ZIP for card certificates and big cards
          const shouldGenerateBoth =
            (values?.certificate_type?._id === 1 &&
              values?.side?.id === 1 &&
              values?.certificate_format?.id === 1 &&
              selectedCardData?.certificate_archive_enabled === true) ||
            (values?.certificate_type?._id === 2 &&
              values?.certificate_format?.id === 1 &&
              selectedCardData?.certificate_archive_enabled === true);

          if (shouldGenerateBoth) {
            // Generate both ZIP and PDF
            handleBothFormats(values, response?.data?.data);
          } else if (values?.certificate_format?.id === 2) {
            // Image format has no interactive preview/print step, so finalize
            // the certificate creation immediately.
            PrintCertificate(data);
            handleImagePrints(values, response?.data?.data);
          } else {
            // Generate only PDF for other certificate types
            handlePDFPrints(values);
            const timeOut = setTimeout(() => {
              dispatch(updateConfig((state) => (state.clear = false)));
              clearTimeout(timeOut);
            }, 1000);
          }
        } else if (response?.status_code === 422) {
          let errors = response?.errors;
          let errorFields = Object.keys(errors);
          errorFields.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
        } else {
          toast.error("Something went wrong");
        }
      });
    },
  });

  function handleImagePrints(values, responseData) {
    if (values?.certificate_type?._id === 1) {
      if (values?.side?.id === 1) {
        handleVisitingCardImage(responseData);
      }
    } else if (values?.certificate_type?._id === 2) {
      handleBigCardImage(responseData);
    }
  }

  function handleBothFormats(values, responseData) {
    const onZipComplete = () => {
      handlePDFPrints(values);
      const timeOut = setTimeout(() => {
        dispatch(updateConfig((state) => (state.clear = false)));
        clearTimeout(timeOut);
      }, 1000);
    };

    // Wait for DOM to render first, then generate ZIP
    setTimeout(() => {
      if (values?.certificate_type?._id === 1 && values?.side?.id === 1) {
        handleVisitingCardImageForBoth(values, responseData, onZipComplete);
      } else if (values?.certificate_type?._id === 2) {
        handleBigCardImageForBoth(values, responseData, onZipComplete);
      }
    }, 2000);
  }

  function handleBigCardImage(responseData) {
    setTimeout(() => {
      const element = bigFrontPrintRef?.current;

      if (!element) {
        toast.error("No elements in the DOM");
        return;
      }

      const items = element.querySelectorAll(".item-class-big-card");

      if (items.length === 0) {
        toast.error("No elements found with the class ‘item-class-big-card’.");
        return;
      }

      const images = [];
      const zip = new JSZip();

      const processImage = (item, index) => {
        // Find the element that contains "SUMMARY NO"
        const summaryNoElement = Array.from(item?.querySelectorAll("p")).find(
          (p) => p.textContent.trim() === "SUMMARY NO",
        );

        // Access the next sibling of the ":" element, which contains the value "051BDBEKI"
        const summaryNoValue =
          summaryNoElement &&
          summaryNoElement.parentElement
            .querySelectorAll("p")[2]
            .textContent.trim();

        return new Promise((resolve) => {
          domtoimage
            .toJpeg(item, {
              cacheBust: true,
              quality: 1, // Image quality (1 is highest for PNG)
              width: item.clientWidth * 4, // Quadruple width for higher quality
              height: item.clientHeight * 4, // Quadruple height for better DPI
              style: {
                transform: "scale(4)", // Scaling to improve DPI
                transformOrigin: "top left", // Ensure the content doesn’t shift
                width: `${item.clientWidth}px`,
                height: `${item.clientHeight}px`,
              },
              useCORS: true, // Handle external images
            })
            .then((imgData) => {
              if (imgData) {
                images.push(imgData); // Store the generated image
                zip.file(`${summaryNoValue}.JPG`, imgData.split(",")[1], {
                  base64: true,
                });
              }
              resolve();
            })
            .catch((error) => {
              console.error("Error generating image", error);
              resolve(); // Continue even if there’s an error
            });
        });
      };

      const processAllImages = async () => {
        for (let i = 0; i < items.length; i++) {
          await processImage(items[i], i);
        }

        if (images.length === 0) {
          toast.error("No images generated");
          return;
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
          const gliNumbers = responseData?.[0]?.recheck_gli_number
            ?.split("/")
            .filter(Boolean)
            .join("_");
          saveAs(content, `${gliNumbers}.zip`);

          const formData = new FormData();
          formData.append("zip_file", content, `${gliNumbers}.zip`);
          formData.append("receipt_id", responseData?.[0]?.receipt_id || "");
          formData.append(
            "recheck_receipt_id",
            responseData?.[0]?.recheck_receipt_id || "",
          );
          formData.append(
            "gli_number",
            responseData?.[0]?.recheck_gli_number || "",
          );
          formData.append(
            "fresh_gli_number",
            responseData?.[0]?.gli_number || "",
          );
          formData.append("order_type", 3);

          if (selectedCardData?.certificate_archive_enabled === true) {
            uploadKalyanCertificate(formData);
          }
          handleCloseModal();
        });
      };

      processAllImages();
    }, 1000);
  }

  function handleVisitingCardImage(responseData) {
    setTimeout(() => {
      const element = visitingOneFrontPrintRef?.current;

      if (!element) {
        toast.error("No elements in the DOM");
        return;
      }

      const items = element.querySelectorAll(".item-class");

      if (items.length === 0) {
        toast.error("No elements found with the class ‘item-class’.");
        return;
      }

      const images = [];
      const zip = new JSZip();

      const processImage = (item, index) => {
        // Find the element that contains "SUMMARY NO"
        const summaryNoElement = Array.from(item?.querySelectorAll("p")).find(
          (p) => p.textContent.trim() === "SUMMARY NO",
        );

        // Access the next sibling of the ":" element, which contains the value "051BDBEKI"
        const summaryNoValue =
          summaryNoElement &&
          summaryNoElement.parentElement
            .querySelectorAll("p")[2]
            .textContent.trim();

        return new Promise((resolve) => {
          domtoimage
            .toJpeg(item, {
              cacheBust: true,
              quality: 1, // Image quality (1 is highest for PNG)
              width: item.clientWidth * 4, // Quadruple width for higher quality
              height: item.clientHeight * 4, // Quadruple height for better DPI
              style: {
                transform: "scale(4)", // Scaling to improve DPI
                transformOrigin: "top left", // Ensure the content doesn’t shift
                width: `${item.clientWidth}px`,
                height: `${item.clientHeight}px`,
              },
              useCORS: true, // Handle external images
            })
            .then((imgData) => {
              if (imgData) {
                images.push(imgData); // Store the generated image
                zip.file(`${summaryNoValue}.JPG`, imgData.split(",")[1], {
                  base64: true,
                });
              }
              resolve();
            })
            .catch((error) => {
              console.error("Error generating image", error);
              resolve(); // Continue even if there’s an error
            });
        });
      };

      const processAllImages = async () => {
        for (let i = 0; i < items.length; i++) {
          await processImage(items[i], i);
        }

        if (images.length === 0) {
          toast.error("No images generated");
          return;
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
          const gliNumbers = responseData?.[0]?.recheck_gli_number
            ?.split("/")
            .filter(Boolean)
            .join("_");
          saveAs(content, `${gliNumbers}.zip`);

          const formData = new FormData();
          formData.append("zip_file", content, `${gliNumbers}.zip`);
          formData.append("receipt_id", responseData?.[0]?.receipt_id || "");
          formData.append(
            "recheck_receipt_id",
            responseData?.[0]?.recheck_receipt_id || "",
          );
          formData.append(
            "gli_number",
            responseData?.[0]?.recheck_gli_number || "",
          );
          formData.append(
            "fresh_gli_number",
            responseData?.[0]?.gli_number || "",
          );
          formData.append("order_type", 3);

          if (selectedCardData?.certificate_archive_enabled === true) {
            uploadKalyanCertificate(formData);
          }
          handleCloseModal();
        });
      };

      processAllImages();
    }, 1000);
  }

  // Modified version for dual format generation - doesn't close modal
  function handleBigCardImageForBoth(_values, responseData, onComplete) {
    setTimeout(() => {
      const element = bigFrontPrintRef?.current;

      if (!element) {
        console.error("Big card ref not found!");
        toast.error("No elements in the DOM - Big Card");
        onComplete();
        return;
      }

      const items = element.querySelectorAll(".item-class-big-card");

      if (items.length === 0) {
        console.error("No .item-class-big-card elements found");
        toast.error("No elements found with the class 'item-class-big-card'.");
        onComplete();
        return;
      }

      const images = [];
      const zip = new JSZip();

      const processImage = (item) => {
        const summaryNoElement = Array.from(item?.querySelectorAll("p")).find(
          (p) => p.textContent.trim() === "SUMMARY NO",
        );

        const summaryNoValue =
          summaryNoElement &&
          summaryNoElement.parentElement
            .querySelectorAll("p")[2]
            .textContent.trim();

        return new Promise((resolve) => {
          domtoimage
            .toJpeg(item, {
              cacheBust: true,
              quality: 1,
              width: item.clientWidth * 4,
              height: item.clientHeight * 4,
              style: {
                transform: "scale(4)",
                transformOrigin: "top left",
                width: `${item.clientWidth}px`,
                height: `${item.clientHeight}px`,
              },
              useCORS: true,
            })
            .then((imgData) => {
              if (imgData) {
                images.push(imgData);
                zip.file(`${summaryNoValue}.JPG`, imgData.split(",")[1], {
                  base64: true,
                });
              }
              resolve();
            })
            .catch((error) => {
              console.error("Error generating image", error);
              resolve();
            });
        });
      };

      const processAllImages = async () => {
        for (let i = 0; i < items.length; i++) {
          await processImage(items[i]);
        }

        if (images.length === 0) {
          toast.error("No images generated");
          onComplete();
          return;
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
          const gliNumbers = responseData?.[0]?.recheck_gli_number
            ?.split("/")
            .filter(Boolean)
            .join("_");
          saveAs(content, `${gliNumbers}.zip`);

          const formData = new FormData();
          formData.append("zip_file", content, `${gliNumbers}.zip`);
          formData.append("receipt_id", responseData?.[0]?.receipt_id || "");
          formData.append(
            "recheck_receipt_id",
            responseData?.[0]?.recheck_receipt_id || "",
          );
          formData.append(
            "gli_number",
            responseData?.[0]?.recheck_gli_number || "",
          );
          formData.append(
            "fresh_gli_number",
            responseData?.[0]?.gli_number || "",
          );
          formData.append("order_type", 3);

          uploadKalyanCertificate(formData);
          onComplete();
        });
      };

      processAllImages();
    }, 1000);
  }

  // Modified version for dual format generation - doesn't close modal
  function handleVisitingCardImageForBoth(values, responseData, onComplete) {
    setTimeout(() => {
      const element = visitingOneFrontPrintRef?.current;

      if (!element) {
        console.error("Visiting card ref not found!");
        toast.error("No elements in the DOM - Visiting Card");
        onComplete();
        return;
      }

      const items = element.querySelectorAll(".item-class");

      if (items.length === 0) {
        console.error("No .item-class elements found");
        toast.error("No elements found with the class 'item-class'.");
        onComplete();
        return;
      }

      const images = [];
      const zip = new JSZip();

      const processImage = (item) => {
        const summaryNoElement = Array.from(item?.querySelectorAll("p")).find(
          (p) => p.textContent.trim() === "SUMMARY NO",
        );

        const summaryNoValue =
          summaryNoElement &&
          summaryNoElement.parentElement
            .querySelectorAll("p")[2]
            .textContent.trim();

        return new Promise((resolve) => {
          domtoimage
            .toJpeg(item, {
              cacheBust: true,
              quality: 1,
              width: item.clientWidth * 4,
              height: item.clientHeight * 4,
              style: {
                transform: "scale(4)",
                transformOrigin: "top left",
                width: `${item.clientWidth}px`,
                height: `${item.clientHeight}px`,
              },
              useCORS: true,
            })
            .then((imgData) => {
              if (imgData) {
                images.push(imgData);
                zip.file(`${summaryNoValue}.JPG`, imgData.split(",")[1], {
                  base64: true,
                });
              }
              resolve();
            })
            .catch((error) => {
              console.error("Error generating image", error);
              resolve();
            });
        });
      };

      const processAllImages = async () => {
        for (let i = 0; i < items.length; i++) {
          await processImage(items[i]);
        }

        if (images.length === 0) {
          toast.error("No images generated");
          onComplete();
          return;
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
          const gliNumbers = responseData?.[0]?.recheck_gli_number
            ?.split("/")
            .filter(Boolean)
            .join("_");
          saveAs(content, `${gliNumbers}.zip`);

          const formData = new FormData();
          formData.append("zip_file", content, `${gliNumbers}.zip`);
          formData.append("receipt_id", responseData?.[0]?.receipt_id || "");
          formData.append(
            "recheck_receipt_id",
            responseData?.[0]?.recheck_receipt_id || "",
          );
          formData.append(
            "gli_number",
            responseData?.[0]?.recheck_gli_number || "",
          );
          formData.append(
            "fresh_gli_number",
            responseData?.[0]?.gli_number || "",
          );
          formData.append("order_type", 3);

          uploadKalyanCertificate(formData);
          onComplete();
        });
      };

      processAllImages();
    }, 1000);
  }

  function handlePDFPrints(values) {
    if (values?.certificate_type?._id === 1) {
      if (values?.side?.id === 1) {
        handleVistitingTypeOneFrontPrintClick(values);
      } else if (values?.side?.id === 2) {
        handlevisitingTypeOneBackPrintClick(values);
      } else {
        handleVisitingDuplexPrint(values);
      }
    }
    if (values?.certificate_type?._id === 2) {
      handleBigClick();
    }
    if (values?.certificate_type?._id === 3) {
      if (values?.side?.id === 1) {
        handlePapperFrontClick();
      } else if (values?.side?.id === 2) {
        handlePapperBackClick();
      } else {
        handlePapperDuplexPrint();
      }
    }
    if (values?.certificate_type?._id === 4) {
      handleFoldableCardPrint();
    }
  }
  console.log("selectedCardData?.gli_number====", selectedCardData?.gli_number);
  const handleVistitingTypeOneFrontPrintClick = (values) => {
    const paper = getPaperConfig(values?.paper_size?.id);
    const element = visitingOneFrontPrintRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}visiting card certificate front_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [0, 9, 0, 9],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          width: paper.containerWidth,
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          // Capped at 4 (matches Orders). At scale 7 a large A5/A4 job builds one html2canvas
          // canvas that exceeds the browser's max canvas area (~268M px² in Chrome) and the
          // bottom silently drops -> trailing BLANK pages (e.g. 60 A5 cards). 4 keeps the full
          // per-paper item cap (PRINT_ITEM_LIMITS) within the canvas budget.
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: paper.format, orientation: paper.orientation },
        pagebreak: false,
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Visiting Card Certificate - Front", filename);
      });
  };

  // const handleVistitingTypeOneFrontPrintClick = async () => {
  //   const element = visitingOneFrontPrintRef?.current;

  //   // Helper function to wait for all images to load
  //   const loadImages = (element) => {
  //     const images = element.querySelectorAll("img");
  //     const imagePromises = [];

  //     images.forEach((img) => {
  //       if (!img.complete) {
  //         imagePromises.push(
  //           new Promise((resolve, reject) => {
  //             img.onload = resolve;
  //             img.onerror = reject;
  //           })
  //         );
  //       }
  //     });

  //     return Promise.all(imagePromises);
  //   };

  //   try {
  //     // Wait for all images to load
  //     await loadImages(element);

  //     const loadFont = async (fontPath, fontName, fontWeight) => {
  //       const response = await fetch(fontPath);
  //       const blob = await response.blob();
  //       const reader = new FileReader();

  //       return new Promise((resolve, reject) => {
  //         reader.onloadend = () => {
  //           const base64Font = reader.result.split(",")[1]; // Extract base64 string
  //           resolve({ base64Font, fontName, fontWeight });
  //         };
  //         reader.onerror = reject;

  //         // Read the font as DataURL
  //         reader.readAsDataURL(blob);
  //       });
  //     };

  //     // Load fonts with different weights
  //     const regularFont = await loadFont(
  //       Assets.GRAPHIC_REGULAR,
  //       "Graphik",
  //       "normal"
  //     );
  //     const semiboldFont = await loadFont(
  //       Assets.GRAPHIC_SEMIBOLD,
  //       "Graphik",
  //       "600"
  //     );

  //     const pdf = new jsPDF("portrait", "mm", "a4");

  //     pdf.addFileToVFS("Graphik-regular.ttf", regularFont.base64Font);
  //     pdf.addFont("Graphik-regular.ttf", "Graphik", "normal");

  //     pdf.addFileToVFS("Graphik-Semibold.ttf", semiboldFont.base64Font);
  //     pdf.addFont("Graphik-Semibold.ttf", "Graphik", "bold");

  //     pdf.html(element, {
  //       callback: async function (pdf) {
  //         const pdfUrl = pdf.output("bloburl");
  //         window.open(pdfUrl, "_blank");
  //         const fileName = `${
  //                     "visiting card certificate front" +
  //                     "-" +
  //                     moment(Date.now()).format("_MM_DD_YYYY_HH_mm_ss")
  //                   }.pdf`
  //                   pdf.save(fileName);
  //         handleCloseModal();
  //       },
  //       margin: [0, 9, 0, 9],
  //       width: 210 - 18,
  //       x: 2.5,
  //       y: 0,
  //       windowWidth: 720,
  //     });
  //   } catch (error) {
  //     console.error("Error loading images or fonts:", error);
  //   }
  // };

  const handlevisitingTypeOneBackPrintClick = (values) => {
    const paper = getPaperConfig(values?.paper_size?.id);
    const element = visitingOneBackPrintRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}visiting card certificate_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [0, 9, 0, 9],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          width: paper.containerWidth,
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          // Capped at 4 (matches Orders). At scale 7 a large A5/A4 job builds one html2canvas
          // canvas that exceeds the browser's max canvas area (~268M px² in Chrome) and the
          // bottom silently drops -> trailing BLANK pages (e.g. 60 A5 cards). 4 keeps the full
          // per-paper item cap (PRINT_ITEM_LIMITS) within the canvas budget.
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: paper.format, orientation: paper.orientation },
        pagebreak: false,
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Visiting Card Certificate - Back", filename);
      });
  };

  const handlePapperFrontClick = () => {
    const element = papperFrontPrintRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}paper certificate front_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [0, 5, 0, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        // pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Paper Certificate - Front", filename);
      });
  };

  const handlePapperBackClick = () => {
    const element = papperBackPrintRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}paper certificate back_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [5, 5, 5, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Paper Certificate - Back", filename);
      });
  };

  const handleBigClick = () => {
    const element = bigFrontPrintRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}big card certificate front_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [5, 5, 5, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Big Card Certificate - Front", filename);
      });
  };

  const handleVisitingDuplexPrint = (values) => {
    const paper = getPaperConfig(values?.paper_size?.id);
    const element = vistingDuplexRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}visiting card duplex_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [0, 9, 0, 9],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          width: paper.containerWidth,
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: paper.format, orientation: paper.orientation },
        // pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Visiting Card Duplex", filename);
      });
  };

  // const handlevisitingTypeOneBackPrintClick = async () => {
  //   const element = visitingOneBackPrintRef?.current;

  //   // Helper function to wait for all images to load
  //   const loadImages = (element) => {
  //     const images = element.querySelectorAll("img");
  //     const imagePromises = [];

  //     images.forEach((img) => {
  //       if (!img.complete) {
  //         imagePromises.push(
  //           new Promise((resolve, reject) => {
  //             img.onload = resolve;
  //             img.onerror = reject;
  //           })
  //         );
  //       }
  //     });

  //     return Promise.all(imagePromises);
  //   };

  //   try {
  //     // Wait for all images to load
  //     await loadImages(element);

  //     const loadFont = async (fontPath, fontName, fontWeight) => {
  //       const response = await fetch(fontPath);
  //       const blob = await response.blob();
  //       const reader = new FileReader();

  //       return new Promise((resolve, reject) => {
  //         reader.onloadend = () => {
  //           const base64Font = reader.result.split(",")[1]; // Extract base64 string
  //           resolve({ base64Font, fontName, fontWeight });
  //         };
  //         reader.onerror = reject;

  //         // Read the font as DataURL
  //         reader.readAsDataURL(blob);
  //       });
  //     };

  //     // Load fonts with different weights
  //     const regularFont = await loadFont(
  //       Assets.GRAPHIC_REGULAR,
  //       "Graphik",
  //       "normal"
  //     );
  //     const semiboldFont = await loadFont(
  //       Assets.GRAPHIC_SEMIBOLD,
  //       "Graphik",
  //       "600"
  //     );

  //     const pdf = new jsPDF("portrait", "mm", "a4");

  //     pdf.addFileToVFS("Graphik-regular.ttf", regularFont.base64Font);
  //     pdf.addFont("Graphik-regular.ttf", "Graphik", "normal");

  //     pdf.addFileToVFS("Graphik-Semibold.ttf", semiboldFont.base64Font);
  //     pdf.addFont("Graphik-Semibold.ttf", "Graphik", "bold");

  //     pdf.html(element, {
  //       callback: async function (pdf) {

  //         const pdfUrl = pdf.output("bloburl");
  //         window.open(pdfUrl, "_blank");
  //         const fileName = `${
  //           "visiting card certificate" +
  //           "-" +
  //           moment(Date.now()).format("_MM_DD_YYYY_HH_mm_ss")
  //         }.pdf`
  //         pdf.save(fileName);
  //         handleCloseModal();
  //       },
  //       margin: [0, 9, 0, 9],
  //       width: 210 - 18,
  //       x: 2.5,
  //       y: 0,
  //       windowWidth: 720,
  //     });
  //   } catch (error) {
  //     console.error("Error loading images or fonts:", error);
  //   }
  // };

  // const handleVisitingDuplexPrint = async () => {
  //   const element = vistingDuplexRef?.current;

  //   // Helper function to wait for all images to load
  //   const loadImages = (element) => {
  //     const images = element.querySelectorAll("img");
  //     const imagePromises = [];

  //     images.forEach((img) => {
  //       if (!img.complete) {
  //         imagePromises.push(
  //           new Promise((resolve, reject) => {
  //             img.onload = resolve;
  //             img.onerror = reject;
  //           })
  //         );
  //       }
  //     });

  //     return Promise.all(imagePromises);
  //   };

  //   try {
  //     // Wait for all images to load
  //     await loadImages(element);

  //     const loadFont = async (fontPath, fontName, fontWeight) => {
  //       const response = await fetch(fontPath);
  //       const blob = await response.blob();
  //       const reader = new FileReader();

  //       return new Promise((resolve, reject) => {
  //         reader.onloadend = () => {
  //           const base64Font = reader.result.split(",")[1]; // Extract base64 string
  //           resolve({ base64Font, fontName, fontWeight });
  //         };
  //         reader.onerror = reject;

  //         // Read the font as DataURL
  //         reader.readAsDataURL(blob);
  //       });
  //     };

  //     // Load fonts with different weights
  //     const regularFont = await loadFont(
  //       Assets.GRAPHIC_REGULAR,
  //       "Graphik",
  //       "normal"
  //     );
  //     const semiboldFont = await loadFont(
  //       Assets.GRAPHIC_SEMIBOLD,
  //       "Graphik",
  //       "600"
  //     );

  //     const pdf = new jsPDF("portrait", "mm", "a4");

  //     pdf.addFileToVFS("Graphik-regular.ttf", regularFont.base64Font);
  //     pdf.addFont("Graphik-regular.ttf", "Graphik", "normal");

  //     pdf.addFileToVFS("Graphik-Semibold.ttf", semiboldFont.base64Font);
  //     pdf.addFont("Graphik-Semibold.ttf", "Graphik", "bold");

  //     pdf.html(element, {
  //       callback: async function (pdf) {

  //         const pdfUrl = pdf.output("bloburl");
  //         window.open(pdfUrl, "_blank");
  //         const fileName = `${
  //           "visiting card duplex" +
  //           "-" +
  //           moment(Date.now()).format("_MM_DD_YYYY_HH_mm_ss")
  //         }.pdf`
  //         pdf.save(fileName);
  //         handleCloseModal();
  //       },
  //       margin: [0, 9, 0, 9],
  //       width: 210 - 18,
  //       x: 2.5,
  //       y: 0,
  //       windowWidth: 720,
  //     });
  //   } catch (error) {
  //     console.error("Error loading images or fonts:", error);
  //   }
  // };

  const handlePapperDuplexPrint = () => {
    const element = papperDuplexRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}paper certificate duplex_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [0, 5, 0, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Paper Certificate Duplex", filename);
      });
  };

  const handleFoldableCardPrint = () => {
    const element = foldableCardRef?.current;
    const filename = `${isMultiPrint ? "" : selectedCardData?.gli_number ? selectedCardData?.gli_number + "_" : ""}foldable_card_${moment(Date.now()).format("DD_MM_YYYY")}_${moment(Date.now()).format("HH_mm_ss")}.pdf`;
    html2pdf()
      .set({
        margin: [12, 5, 0, 5],
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          dpi: 100,
          letterRendering: true,
          useCORS: true,
          logging: true,
          scale: 4,
          scrollY: 0,
        },
        pagebreak: { avoid: "", mode: ["avoid-all", "css", "legacy"] },
        jsPDF: { unit: "mm", format: "a5", orientation: "landscape" },
        DisablePdfCompression: true,
      })
      .from(element)
      .outputPdf()
      .get("pdf")
      .then(function (pdfObj) {
        showPdfPreview(pdfObj, "Foldable Card", filename);
      });
  };

  const handleCloseModal = () => {
    dispatch(
      updateConfig((state) => {
        state.printModal = false;
        // Reset to A4 on close (print or cancel) so a chosen A3/A5 never carries
        // into the shared visiting templates used by other print paths.
        state.paper_size = DEFAULT_PAPER_SIZE_ID;
      }),
    );
    setIsLoading(false);
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const setCardSize = (value) => {
    formik?.setFieldValue("card_size", value || null);
    dispatch(
      updateConfig((state) => {
        state.card_size = value?.id;
      }),
    );
  };

  // Keeps formik and the slice in sync so the shared visiting templates
  // (NewFrontCard / Back / NewVisitingDuplex) read the same paper size at print time.
  const setPaperSize = (value) => {
    formik?.setFieldValue("paper_size", value || null);
    dispatch(
      updateConfig((state) => {
        state.paper_size = value?.id ?? DEFAULT_PAPER_SIZE_ID;
      }),
    );
  };

  let reportListData = basicData?.data?.report_list?.filter(
    (item) => item?.certificate_type === formik?.values?.certificate_type?._id,
  );

  // Card-Certificate (type 1) item-count limit per paper size + side. Reactive to the Side and
  // Paper Size selects so the modal can show an inline error and disable Print before submit.
  const selectedItemCount = selectedItems?.length ?? 0;
  const printItemLimit =
    formik?.values?.certificate_type?._id === 1 && formik?.values?.side?.id
      ? getPrintItemLimit(
          formik?.values?.paper_size?.id,
          formik?.values?.side?.id,
        )
      : null;
  const isPrintLimitExceeded =
    printItemLimit != null && selectedItemCount > printItemLimit;
  const printLimitError = isPrintLimitExceeded
    ? `Maximum ${printItemLimit} items allowed for ${
        formik?.values?.paper_size?.name ?? ""
      } ${
        formik?.values?.side?.id === 3 ? "duplex" : "front/back"
      } — you have selected ${selectedItemCount}.`
    : "";

  return {
    formik,
    basicData,
    handleCloseModal,
    getFieldError,
    reportListData,
    setCardSize,
    setPaperSize,
    isLoading,
    setIsLoading,
    previewState,
    handleConfirmPrint,
    handleCancelPreview,
    printLimitError,
    isPrintLimitExceeded,
  };
};

export default usePrintType;
